import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Modal } from './Modal'
import { carePlanService } from '../services/carePlanService'
import { userService } from '../services/userService'
import { User } from '../types/user'
import './CarePlanTransferModal.css'

interface CarePlanTransferModalProps {
  isOpen: boolean
  onClose: () => void
  carePlanId: string
  onSuccess?: () => void
}

export function CarePlanTransferModal({
  isOpen,
  onClose,
  carePlanId,
  onSuccess,
}: CarePlanTransferModalProps) {
  const queryClient = useQueryClient()
  const [selectedProviderId, setSelectedProviderId] = useState<string>('')
  const [reason, setReason] = useState<string>('')
  const [error, setError] = useState<string | null>(null)

  const { data: providers, isLoading: isLoadingProviders } = useQuery({
    queryKey: ['clinical-staff'],
    queryFn: () => userService.getClinicalStaff(),
    enabled: isOpen,
  })

  const transferMutation = useMutation({
    mutationFn: ({ targetProviderId, reason }: { targetProviderId: string; reason?: string }) =>
      carePlanService.transfer(carePlanId, targetProviderId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['care-plans'] })
      queryClient.invalidateQueries({ queryKey: ['care-plan', carePlanId] })
      setSelectedProviderId('')
      setReason('')
      setError(null)
      onSuccess?.()
      onClose()
    },
    onError: (err: any) => {
      setError(err?.response?.data?.message || err?.message || 'Failed to transfer care plan')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!selectedProviderId) {
      setError('Please select a provider')
      return
    }

    transferMutation.mutate({
      targetProviderId: selectedProviderId,
      reason: reason.trim() || undefined,
    })
  }

  if (!isOpen) return null

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Transfer Care Plan to Provider" size="medium">
      <form onSubmit={handleSubmit} className="care-plan-transfer-form">
        <div className="form-group">
          <label htmlFor="provider-select">
            Provider <span className="required">*</span>
          </label>
          {isLoadingProviders ? (
            <div className="loading-text">Loading providers...</div>
          ) : (
            <select
              id="provider-select"
              value={selectedProviderId}
              onChange={(e) => setSelectedProviderId(e.target.value)}
              className="form-control"
              required
              disabled={transferMutation.isPending}
            >
              <option value="">Select a provider...</option>
              {providers?.map((provider: User) => (
                <option key={provider.id} value={provider.id}>
                  {provider.firstName} {provider.lastName}
                  {provider.role && ` - ${provider.role}`}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="transfer-reason">Reason (Optional)</label>
          <textarea
            id="transfer-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="form-control"
            rows={4}
            placeholder="Optional: Provide a reason for transferring this care plan..."
            disabled={transferMutation.isPending}
          />
        </div>

        {error && (
          <div className="form-error" role="alert">
            {error}
          </div>
        )}

        <div className="modal-footer">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={transferMutation.isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={transferMutation.isPending || !selectedProviderId || isLoadingProviders}
          >
            {transferMutation.isPending ? 'Transferring...' : 'Transfer Care Plan'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
